import { memo } from "react";
import Ad from "./../Ad/component";
import { buildMarkupFromParsed } from "../../utils/storyBodyMarkupBuilder/library";

import { BIGBOX_DOUBLE, IN_READ, OCELOT, BIGBOX, READ } from "./constants";
import CTA from "../CTA/component";
import { createFeatureFlagSelector } from "../../selectors/featureFlag";
import { featureFlagFindAll } from "../../utils/flags/library";
import { getCTA, isCTA } from "../CTA/utils";
import { BUTTON_VARIANT } from "../CTA/constants";

let prevMarkupType = "";

const hasPolopolyElement = ({ type = "", content }, checkNestedFn) => {
    if (type.startsWith("polopoly_")) return true;
    if (!Array.isArray(content)) return false;
    return checkNestedFn(content);
};

const hasNestedPolopolyElement = (content) =>
    content.some((children) =>
        hasPolopolyElement(children, hasNestedPolopolyElement)
    );

export const shouldReplaceTagWithDiv = (node) => {
    if (node.tag === "table") {
        return false;
    }
    return hasPolopolyElement(node, hasNestedPolopolyElement);
};

function StoryBody({
    body,
    adData,
    adIndexArray,
    hasLeadMediaClipID,
    isSponsoredContent,
    section,
    featureflags,
    content,
}) {
    let initialMediaPosition;
    initialMediaPosition = hasLeadMediaClipID === true ? 2 : 1;

    const getMediaPosition = () => initialMediaPosition++;
    const adsPositionArray = Array.isArray(adIndexArray) ? adIndexArray : [];
    let currentAdIndex = 0;
    let paragraphCount = 0;
    const ctasIndexesAlreadyInjected = new Set();

    const storySelector = createFeatureFlagSelector("story");
    const ctaFeatureList = storySelector({ featureflags });
    const ctas = featureFlagFindAll(section, ctaFeatureList)
        .map((args) => {
            // if the CTA has a variant, use it, otherwise default to BUTTON_VARIANT
            return { variant: BUTTON_VARIANT, ...args.cta };
        })
        .filter(isCTA)
        .reduce(getCTA(content?.tags, content?.concepts), []);

    const buildAd = () => {
        const adType = currentAdIndex === 0 ? BIGBOX_DOUBLE : IN_READ;
        const props = {
            type: adType,
            key: `${adType}-${currentAdIndex}`,
            lazy: true,
            targeting: Object.assign(
                {
                    type: OCELOT,
                    contentId: adData.contentId,
                    rmtype: BIGBOX,
                    plc:
                        currentAdIndex === 0
                            ? READ
                            : `${READ}${currentAdIndex + 1}`,
                },
                adData.targeting
            ),
            responsive: true,
            exclusions: adData.exclusions,
        };

        return <Ad {...props} />;
    };

    const parsedBody = body.reduce((storyBody, node) => {
        const replaceTagWithDiv = shouldReplaceTagWithDiv(node);
        const markup = buildMarkupFromParsed({
            ...node,
            getMediaPosition,
            replaceTagWithDiv,
            isSponsoredContent,
        });

        if (markup?.type === "p") {
            paragraphCount++;
        }
        if (adsPositionArray[currentAdIndex] === paragraphCount) {
            if (prevMarkupType !== "h2" && prevMarkupType !== "h3") {
                storyBody.push(buildAd());
            }
            currentAdIndex++;
        }
        storyBody.push(markup);

        // check if CTAs exist and should be injected at this point
        ctas.forEach((cta, index) => {
            const tagMatchesCta = markup?.type === cta.tag;
            const tagPositionIsCorrect =
                !!cta.tagPositionAfter &&
                cta.tagPositionAfter === paragraphCount;
            if (tagMatchesCta && tagPositionIsCorrect) {
                storyBody.push(<CTA key={`cta-${index}`} {...cta} />);
                ctasIndexesAlreadyInjected.add(index);
            }
        });

        prevMarkupType = markup.type;

        return storyBody;
    }, []);

    // inject all the tagPositionLast CTAs, or those that weren't injected due to short story length
    ctas.forEach((cta, index) => {
        if (!!cta.tagPositionLast || !ctasIndexesAlreadyInjected.has(index)) {
            parsedBody.push(<CTA key={`cta-${index}`} {...cta} />);
        }
    });

    return (
        <div className="story" data-testid="story-body">
            {parsedBody}
        </div>
    );
}

const isEqual = (prevProps, nextProps) => {
    return prevProps.adData?.contentId === nextProps.adData?.contentId;
};

export default memo(StoryBody, isEqual);
